// src/realtime/usePGBroadcastRefresh.ts
import * as React from "react";
import {
  getPGBroadcastClient,
  pgSubscribe,
  pgUnsubscribe,
} from "./pgbroadcastSingleton";

type Ev = { type?: string; topic?: string; data?: any };

export function usePGBroadcastRefresh(opts: {
  topics: string[];
  onChange: () => void;
  enabled?: boolean;
  debounceMs?: number;
}) {
  const { topics, onChange, enabled = true, debounceMs = 150 } = opts;

  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  const timerRef = React.useRef<number | null>(null);

  const topicsKey = React.useMemo(() => {
    return Array.from(new Set(topics)).filter(Boolean).sort().join("\n");
  }, [topics]);

  const cancel = React.useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const trigger = React.useCallback(() => {
    cancel();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      onChangeRef.current();
    }, debounceMs);
  }, [cancel, debounceMs]);

  React.useEffect(() => {
    if (!enabled) return;

    const c = getPGBroadcastClient();
    const list = topicsKey ? topicsKey.split("\n") : [];
    const set = new Set(list);

    for (const t of list) pgSubscribe(t);

    const off = c.onMessage((ev: Ev) => {
      if (ev?.type !== "change") return;
      const t = ev?.topic;
      if (!t || !set.has(t)) return;
      trigger();
    });

    return () => {
      off();
      for (const t of list) pgUnsubscribe(t);
      cancel();
    };
  }, [enabled, topicsKey, trigger, cancel]);
}
